import { SagaIterator } from '@redux-saga/core';
import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { statusMessageSaga } from 'pages/AppManagement/saga';
import { getAssetFormData } from 'pages/FeedManagement/mapping';
import { RequestSagaParams } from '../../types';
import * as Actions from './slice';
import * as Endpoints from './endpoints';

export function* fetchCourseList({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getCoursesList, payload);
    yield put(Actions.fetchCourseListSuccess(response.data));
  } catch (error: any) {
    yield put(Actions.fetchCourseListFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* getCourse({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getCourse, payload.courseId);
    yield put(Actions.fetchCourseSuccess(response.data));
  } catch (error: any) {
    yield put(Actions.fetchCourseFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* createCourse({ payload }: RequestSagaParams): SagaIterator {
  try {
    const data = { ...payload.data };
    let icon = undefined;
    if (data.icon instanceof File) {
      icon = data.icon;
      delete data.icon;
    }
    let response = yield call(Endpoints.createCourse, data);

    if (icon instanceof File) {
      const mediaUploadForm = new FormData();
      mediaUploadForm.append('files', icon);
      const logoUploadResponse = yield call(Endpoints.putMediaContent, mediaUploadForm, response.data.uid, 'course');
      response.data.icon = logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;

      response = yield call(Endpoints.updateCourse, response.data.uid, response.data);
    }

    yield put(Actions.createCourseSuccess(response.data));
    if (payload.callBack) {
      yield call(payload.callBack, response.data.uid);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully created the course' });
  } catch (error: any) {
    yield put(Actions.createCourseFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* updateCourse({ payload }: RequestSagaParams): SagaIterator {
  try {
    const data = { ...payload.data };
    if (data.icon && data.icon instanceof File) {
      const mediaUploadForm = new FormData();
      mediaUploadForm.append('files', data.icon);
      const logoUploadResponse = yield call(Endpoints.putMediaContent, mediaUploadForm, payload.id, 'course');
      data.icon = logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
    }

    const response = yield call(Endpoints.updateCourse, payload.id, data);
    yield put(Actions.updateCourseSuccess(response.data));
    if (payload.callBack) {
      yield call(payload.callBack, response.data.uid);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully updated the course' });
  } catch (error: any) {
    yield put(Actions.updateCourseFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* publishCourse({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.publishCourse, payload.id);
    yield put(Actions.publishCourseSuccess(response.data));
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully published the course' });
  } catch (error: any) {
    yield put(Actions.publishCourseFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* deleteCourse({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.deleteCourse, payload.id);
    yield put(Actions.deleteCourseSuccess({}));
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    const state = yield select();
    yield put(Actions.fetchCourseList(state.courseManagement.page));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully deleted the course' });
  } catch (error: any) {
    yield put(Actions.deleteCourseFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* archiveCourse({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.archiveCourse, payload.id);
    yield put(Actions.archiveCourseSuccess(response.data));
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully archived the course' });
  } catch (error: any) {
    yield put(Actions.archiveCourseFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* createTrack({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.createTrack, payload.courseId, payload.data);
    yield put(Actions.createTrackSuccess(response.data));
    if (payload.callBack) {
      yield call(payload.callBack, response.data);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully created the track' });
  } catch (error: any) {
    yield put(Actions.createTrackFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* updateTrack({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.updateTrack, payload.courseId, payload.id, payload.data);
    yield put(Actions.updateTrackSuccess(response.data));
    if (payload.callBack) {
      yield call(payload.callBack, response.data);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully updated the track' });
  } catch (error: any) {
    yield put(Actions.updateTrackFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* deleteTrack({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.deleteTrack, payload.courseId, payload.id);
    yield put(Actions.deleteTrackSuccess({ trackId: payload.id }));
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully deleted the track' });
  } catch (error: any) {
    yield put(Actions.deleteTrackFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* getTrack({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getTrack, payload.courseId, payload.id);
    yield put(Actions.getTrackSuccess({ contents: response.data.contents, trackId: payload.id }));
    if (payload.callback) {
      yield call(payload.callback, response.data);
    }
  } catch (error: any) {
    yield put(Actions.getTrackFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* getTracks({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getTracks, payload.courseId);
    yield put(Actions.getTracksSuccess(response.data));
  } catch (error: any) {
    yield put(Actions.getTracksFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* getTrackConcepts({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getTrackConcepts, payload.courseId, payload.id);
    yield put(Actions.getTrackConceptsSuccess({}));
    if (payload.callBack) {
      yield call(payload.callBack, response.data.concepts);
    }
  } catch (error: any) {
    yield put(Actions.getTrackConceptsFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* reorderTracks({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.reorderTracks, payload.courseId, payload.data);
    yield put(Actions.reorderTracksSuccess({}));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully reordered the tracks' });
  } catch (error: any) {
    yield put(Actions.reorderTracksFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* getCourseStatus({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getCourseStatus);
    yield put(Actions.getCourseStatusSuccess(response.data));
  } catch (error: any) {
    yield put(Actions.getCourseStatusFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* toggleCourseStatus({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.toggleCourseStatus, { enabled: payload.enabled });
    yield put(Actions.toggleCourseStatusSuccess(response.data));
    yield call(statusMessageSaga, {
      type: 'success',
      message: `${
        payload.enabled ? 'Course activation completed successfully' : 'Course deactivation completed successfully'
      }`
    });
  } catch (error: any) {
    yield put(Actions.toggleCourseStatusFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  }
}

export function* CourseManagementWatcherSaga(): SagaIterator {
  yield all([yield takeLatest(Actions.fetchCourseList.type, fetchCourseList)]);
  yield all([yield takeLatest(Actions.fetchCourse.type, getCourse)]);
  yield all([yield takeLatest(Actions.createCourse.type, createCourse)]);
  yield all([yield takeLatest(Actions.updateCourse.type, updateCourse)]);
  yield all([yield takeLatest(Actions.publishCourse.type, publishCourse)]);
  yield all([yield takeLatest(Actions.archiveCourse.type, archiveCourse)]);
  yield all([yield takeLatest(Actions.deleteCourse.type, deleteCourse)]);
  yield all([yield takeLatest(Actions.createTrack.type, createTrack)]);
  yield all([yield takeLatest(Actions.updateTrack.type, updateTrack)]);
  yield all([yield takeLatest(Actions.deleteTrack.type, deleteTrack)]);
  yield all([yield takeLatest(Actions.getTrack.type, getTrack)]);
  yield all([yield takeLatest(Actions.getTracks.type, getTracks)]);
  yield all([yield takeLatest(Actions.getTrackConcepts.type, getTrackConcepts)]);
  yield all([yield takeLatest(Actions.reorderTracks.type, reorderTracks)]);
  yield all([yield takeLatest(Actions.getCourseStatus.type, getCourseStatus)]);
  yield all([yield takeLatest(Actions.toggleCourseStatus.type, toggleCourseStatus)]);
}

export default CourseManagementWatcherSaga;
